import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coursesAPI } from '../../services/api';
import { getCourseThumbnail } from '../../utils/courseMedia';

const CourseDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [expandedModule, setExpandedModule] = useState(0);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 900);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user') || 'null');
        setUser(userData);

        const fetchCourse = async () => {
            try {
                const response = await coursesAPI.getById(id);
                const data = response.data;
                const fullJson = data.full_json || {};
                const normalized = {
                    ...data,
                    duration: data.duration || fullJson.duration,
                    prerequisites: data.prerequisites || fullJson.prerequisites,
                    learningObjectives: data.learningObjectives || fullJson.learningObjectives,
                    tags: data.tags || fullJson.tags,
                    language: data.language || fullJson.language,
                    modules: data.modules || fullJson.modules || []
                };
                setCourse(normalized);
            } catch (error) {
                console.error("Error loading course", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCourse();
    }, [id]);

    const handleEnroll = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        navigate(`/courses/${id}/learn`);
    };

    const handlePreview = () => {
        const previewSection = document.getElementById('course-preview');
        if (previewSection) {
            previewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getEmbedUrl = (url) => {
        if (!url) return null;
        try {
            const parsed = new URL(url);
            if (parsed.hostname.includes('youtu.be')) {
                const videoId = parsed.pathname.replace('/', '').trim();
                return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
            }
            if (parsed.hostname.includes('youtube.com')) {
                const videoId = parsed.searchParams.get('v');
                return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
            }
            return url;
        } catch (error) {
            return null;
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>Loading course details...</p>
            </div>
        );
    }

    if (!course) {
        return (
            <div style={styles.notFoundContainer}>
                <div style={styles.notFoundIcon}>📚</div>
                <h2 style={styles.notFoundTitle}>Course Not Found</h2>
                <p style={styles.notFoundText}>
                    The course you are looking for does not exist or has been removed.
                </p>
                <button onClick={() => navigate('/courses')} style={styles.backButton}>
                    ← Browse All Courses
                </button>
            </div>
        );
    }

    const previewLesson = course.modules?.flatMap(module => module.lessons || []).find(lesson => lesson.videoUrl) || null;
    const previewUrl = getEmbedUrl(previewLesson?.videoUrl);

    return (
        <div style={styles.pageContainer}>
            {/* Hero Section */}
            <div style={styles.hero}>
                <div style={styles.heroOverlay}></div>
                {getCourseThumbnail(course) && (
                    <img src={getCourseThumbnail(course)} alt={course.title} style={styles.heroImage} />
                )}
                <div style={styles.heroContent}>
                    <div className="container">
                        <button onClick={() => navigate('/courses')} style={styles.breadcrumb}>
                            ← Back to Courses
                        </button>
                        <h1 style={styles.courseTitle}>{course.title}</h1>
                        <p style={styles.courseDescription}>{course.description}</p>
                        <div style={styles.heroMeta}>
                            <span style={styles.metaItem}>
                                <span style={styles.metaIcon}>📊</span>
                                {course.level || 'All Levels'}
                            </span>
                            <span style={styles.metaItem}>
                                <span style={styles.metaIcon}>⏱️</span>
                                {course.duration || 'Self-paced'}
                            </span>
                            {course.modules && (
                                <span style={styles.metaItem}>
                                    <span style={styles.metaIcon}>📚</span>
                                    {course.modules.length} Modules
                                </span>
                            )}
                            <span style={styles.metaItem}>
                                <span style={styles.metaIcon}>🌐</span>
                                {course.language || 'English'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container" style={styles.mainContent}>
                <div style={{...styles.contentGrid, gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 2.5fr) minmax(0, 1fr)' }}>
                    {/* Left Column - Course Details */}
                    <div style={styles.leftColumn}>
                        {/* Preview Player */}
                        <div id="course-preview" style={styles.section}>
                            <div style={styles.previewHeader}>
                                <h2 style={styles.sectionTitle}>Course Preview</h2>
                                <span style={styles.previewBadge}>Free Preview</span>
                            </div>
                            <div style={styles.videoShell}>
                                {previewUrl ? (
                                    <iframe
                                        title="Course preview"
                                        src={previewUrl}
                                        style={styles.videoFrame}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                ) : (
                                    <div style={styles.videoFallback}>
                                        <div style={styles.videoFallbackIcon}>🎬</div>
                                        <div style={styles.videoFallbackText}>Preview video coming soon</div>
                                    </div>
                                )}
                            </div>
                            <div style={styles.previewMeta}>
                                <div style={styles.previewTitle}>
                                    {previewLesson?.title || 'Introduction to the course'}
                                </div>
                                <div style={styles.previewSubtitle}>
                                    {previewLesson?.description || 'Get a quick look at the teaching style and structure.'}
                                </div>
                            </div>
                        </div>

                        {/* What You'll Learn */}
                        {course.learningObjectives && course.learningObjectives.length > 0 && (
                            <div style={styles.section}>
                                <h2 style={styles.sectionTitle}>What You'll Learn</h2>
                                <div style={styles.objectivesList}>
                                    {course.learningObjectives.map((objective, index) => (
                                        <div key={index} style={styles.objectiveItem}>
                                            <span style={styles.checkIcon}>✓</span>
                                            <span>{objective}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Prerequisites */}
                        {course.prerequisites && (
                            <div style={styles.section}>
                                <h2 style={styles.sectionTitle}>Prerequisites</h2>
                                <p style={styles.prerequisitesText}>{course.prerequisites}</p>
                            </div>
                        )}

                        {/* Course Curriculum */}
                        {course.modules && course.modules.length > 0 && (
                            <div style={styles.section}>
                                <h2 style={styles.sectionTitle}>Course Curriculum</h2>
                                <div style={styles.curriculumList}>
                                    {course.modules.map((module, moduleIndex) => (
                                        <div key={moduleIndex} style={styles.moduleCard}>
                                            <div 
                                                style={styles.moduleHeader}
                                                onClick={() => setExpandedModule(
                                                    expandedModule === moduleIndex ? -1 : moduleIndex
                                                )}
                                            >
                                                <div style={styles.moduleHeaderLeft}>
                                                    <span style={styles.moduleNumber}>
                                                        Module {moduleIndex + 1}
                                                    </span>
                                                    <span style={styles.moduleTitle}>{module.title}</span>
                                                </div>
                                                <span style={styles.expandIcon}>
                                                    {expandedModule === moduleIndex ? '−' : '+'}
                                                </span>
                                            </div>
                                            
                                            {expandedModule === moduleIndex && (
                                                <div style={styles.moduleContent}>
                                                    <p style={styles.moduleDescription}>{module.description}</p>
                                                    {module.lessons && module.lessons.length > 0 && (
                                                        <div style={styles.lessonsList}>
                                                            {module.lessons.map((lesson, lessonIndex) => (
                                                                <div key={lessonIndex} style={styles.lessonItem}>
                                                                    <div style={styles.lessonHeader}>
                                                                        <span style={styles.lessonIcon}>
                                                                            {lesson.videoUrl ? '▶️' : '📄'}
                                                                        </span>
                                                                        <span style={styles.lessonTitle}>
                                                                            {lesson.title}
                                                                        </span>
                                                                    </div>
                                                                    {lesson.duration && (
                                                                        <span style={styles.lessonDuration}>
                                                                            {lesson.duration}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tags */}
                        {course.tags && course.tags.length > 0 && (
                            <div style={styles.section}>
                                <h2 style={styles.sectionTitle}>Tags</h2>
                                <div style={styles.tagsList}>
                                    {course.tags.map((tag, index) => (
                                        <span key={index} style={styles.tag}>{tag}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Enrollment Card */}
                    <div style={styles.rightColumn}>
                        <div style={styles.enrollmentCard}>
                            {getCourseThumbnail(course) && (
                                <img src={getCourseThumbnail(course)} alt={course.title} style={styles.cardImage} />
                            )}
                            <div style={styles.cardContent}>
                                <div style={styles.priceSection}>
                                    {course.price ? (
                                        <>
                                            <span style={styles.price}>₹{course.price}</span>
                                            <span style={styles.priceLabel}>One-time payment</span>
                                        </>
                                    ) : (
                                        <>
                                            <span style={styles.price}>Free</span>
                                            <span style={styles.priceLabel}>Full access included</span>
                                        </>
                                    )}
                                </div>

                                <button onClick={handleEnroll} style={styles.enrollButton}>
                                    {user ? 'Start Learning' : 'Sign In to Enroll'}
                                </button>

                                <button onClick={handlePreview} style={styles.previewButton}>
                                    Watch Preview
                                </button>

                                <div style={styles.includes}>
                                    <h4 style={styles.includesTitle}>This course includes:</h4>
                                    <div style={styles.includesList}>
                                        {course.duration && (
                                            <div style={styles.includeItem}>
                                                <span>⏱️</span>
                                                <span>{course.duration} of content</span>
                                            </div>
                                        )}
                                        {course.modules && (
                                            <div style={styles.includeItem}>
                                                <span>📚</span>
                                                <span>{course.modules.length} modules</span>
                                            </div>
                                        )}
                                        <div style={styles.includeItem}>
                                            <span>📱</span>
                                            <span>Mobile and desktop access</span>
                                        </div>
                                        <div style={styles.includeItem}>
                                            <span>🎓</span>
                                            <span>Certificate of completion</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    pageContainer: {
        minHeight: '100vh',
        backgroundColor: '#f9fafb'
    },
    loadingContainer: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '4px solid #f3f4f6',
        borderTop: '4px solid #4f46e5',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
    },
    loadingText: {
        fontSize: '16px',
        color: '#6b7280'
    },
    notFoundContainer: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb',
        padding: '40px 20px'
    },
    notFoundIcon: {
        fontSize: 'clamp(48px, 10vw, 80px)',
        marginBottom: '24px'
    },
    notFoundTitle: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '12px'
    },
    notFoundText: {
        fontSize: '16px',
        color: '#6b7280',
        marginBottom: '32px',
        textAlign: 'center',
        maxWidth: '400px'
    },
    backButton: {
        padding: '12px 32px',
        backgroundColor: '#4f46e5',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    hero: {
        position: 'relative',
        minHeight: '400px',
        backgroundColor: '#1e293b',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden'
    },
    heroImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0.6
    },
    heroOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.70) 100%)'
    },
    heroContent: {
        position: 'relative',
        zIndex: 1,
        width: '100%',
        padding: 'clamp(30px, 5vw, 60px) 0'
    },
    breadcrumb: {
        color: '#94a3b8',
        fontSize: '14px',
        marginBottom: '20px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '8px 0',
        transition: 'color 0.2s'
    },
    courseTitle: {
        fontSize: 'clamp(28px, 5vw, 48px)',
        fontWeight: '800',
        color: 'white',
        marginBottom: '16px',
        lineHeight: '1.2'
    },
    courseDescription: {
        fontSize: '20px',
        color: '#cbd5e1',
        marginBottom: '32px',
        lineHeight: '1.6',
        maxWidth: '800px'
    },
    heroMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px'
    },
    metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#e2e8f0',
        fontSize: '16px',
        fontWeight: '500'
    },
    metaIcon: {
        fontSize: '20px'
    },
    mainContent: {
        padding: '60px 20px'
    },
    contentGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '20px',
        alignItems: 'start'
    },
    leftColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '40px'
    },
    rightColumn: {
        position: 'sticky',
        top: '100px'
    },
    section: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: 'clamp(16px, 3vw, 32px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
    },
    sectionTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '24px'
    },
    objectivesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    objectiveItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        fontSize: '16px',
        color: '#374151',
        lineHeight: '1.6'
    },
    checkIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        backgroundColor: '#dcfce7',
        color: '#16a34a',
        borderRadius: '50%',
        fontSize: '14px',
        fontWeight: '700',
        flexShrink: 0
    },
    prerequisitesText: {
        fontSize: '16px',
        color: '#4b5563',
        lineHeight: '1.7'
    },
    curriculumList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    moduleCard: {
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#f9fafb'
    },
    moduleHeader: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'background 0.2s',
        backgroundColor: 'white'
    },
    moduleHeaderLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    moduleNumber: {
        fontSize: '12px',
        fontWeight: '700',
        color: '#6366f1',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    },
    moduleTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1f2937'
    },
    expandIcon: {
        fontSize: '24px',
        color: '#6b7280',
        fontWeight: '300'
    },
    moduleContent: {
        padding: '24px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: 'white'
    },
    moduleDescription: {
        fontSize: '15px',
        color: '#6b7280',
        lineHeight: '1.6',
        marginBottom: '20px'
    },
    lessonsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    lessonItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        transition: 'background 0.2s'
    },
    lessonHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    lessonIcon: {
        fontSize: '16px'
    },
    lessonTitle: {
        fontSize: '15px',
        color: '#374151',
        fontWeight: '500'
    },
    lessonDuration: {
        fontSize: '13px',
        color: '#9ca3af'
    },
    tagsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    tag: {
        padding: '6px 16px',
        backgroundColor: '#eff6ff',
        color: '#1e40af',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '600'
    },
    previewHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
    },
    previewBadge: {
        padding: '6px 12px',
        borderRadius: '999px',
        backgroundColor: '#ecfeff',
        color: '#0e7490',
        fontSize: '12px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.04em'
    },
    videoShell: {
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        borderRadius: '14px',
        overflow: 'hidden',
        backgroundColor: '#0f172a',
        boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)'
    },
    videoFrame: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        border: '0'
    },
    videoFallback: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#e2e8f0',
        gap: '8px'
    },
    videoFallbackIcon: {
        fontSize: '40px'
    },
    videoFallbackText: {
        fontSize: '14px',
        color: '#cbd5f5'
    },
    previewMeta: {
        marginTop: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    previewTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#111827'
    },
    previewSubtitle: {
        fontSize: '14px',
        color: '#6b7280',
        lineHeight: '1.6'
    },
    enrollmentCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '1px solid #e5e7eb'
    },
    cardImage: {
        width: '100%',
        height: '200px',
        objectFit: 'cover'
    },
    cardContent: {
        padding: 'clamp(16px, 3vw, 32px)'
    },
    priceSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '24px'
    },
    price: {
        fontSize: '36px',
        fontWeight: '800',
        color: '#10b981'
    },
    priceLabel: {
        fontSize: '14px',
        color: '#6b7280'
    },
    enrollButton: {
        width: '100%',
        padding: '16px',
        backgroundColor: '#4f46e5',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: '24px'
    },
    previewButton: {
        width: '100%',
        padding: '14px',
        backgroundColor: '#e0e7ff',
        color: '#3730a3',
        border: 'none',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        marginBottom: '24px'
    },
    includes: {
        borderTop: '1px solid #e5e7eb',
        paddingTop: '24px'
    },
    includesTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '16px'
    },
    includesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    includeItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '14px',
        color: '#4b5563'
    }
};

export default CourseDetail;
